import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { administratorApi } from '../utils/administratorApi';

const Page = styled.section`
  --ink: ${({ theme }) => theme.isDarkMode ? '#edf0f5' : '#202631'};
  --muted: ${({ theme }) => theme.isDarkMode ? '#a0a8b6' : '#747d8b'};
  --line: ${({ theme }) => theme.isDarkMode ? '#343b46' : '#e9ecf1'};
  --soft: ${({ theme }) => theme.isDarkMode ? '#262c35' : '#f7f8fa'};
  width: min(1120px, calc(100% - 64px)); margin: 38px auto 56px; color: var(--ink); font-size: 14px;
  &, & * { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Microsoft YaHei', sans-serif; }
  h1 { margin: 0; font-size: 28px; font-weight: 700; }
  p { color: var(--muted); line-height: 1.7; margin: 12px 0 22px; }
  button { min-height: 40px; padding: 0 15px; border: 1px solid var(--line); border-radius: 9px; font: inherit; cursor: pointer; background: ${({ theme }) => theme.cardBg}; color: var(--ink); }
  button:hover:not(:disabled) { background: var(--soft); }
  button:disabled { opacity: .5; cursor: not-allowed; }
  button.primary { background: #ff4d4f; color: #fff; border-color: #ff4d4f; }
  button.danger { color: #df4149; }
  button:focus-visible, input:focus-visible { outline: 3px solid #ff4d4f55; outline-offset: 2px; }
  .muted { color: var(--muted); }
  .summary { display: flex; gap: 12px; flex-wrap: wrap; margin-bottom: 24px; }
  .summary span { padding: 9px 14px; border-radius: 9px; border: 1px solid var(--line); background: ${({ theme }) => theme.cardBg}; }
  .summary b { margin-left: 9px; font-variant-numeric: tabular-nums; }
  .notice { padding: 14px 18px; margin: 16px 0; border-radius: 9px; background: var(--soft); line-height: 1.6; }
  .error { color: #df4149; }
  @media(max-width: 760px) { width: calc(100% - 32px); margin-top: 24px; h1 { font-size: 24px; } }
`;
const Panel = styled.div`
  background: ${({ theme }) => theme.cardBg}; border: 1px solid var(--line); border-radius: 16px; overflow: hidden;
  form { display: flex; gap: 10px; flex-wrap: wrap; padding: 22px 24px; align-items: center; }
  input { flex: 1; min-width: 180px; max-width: 410px; height: 42px; padding: 0 13px; border: 1px solid var(--line); border-radius: 9px; font: inherit; background: var(--soft); color: var(--ink); }
  .caption { padding: 12px 24px; background: var(--soft); color: var(--muted); line-height: 1.5; }
  .empty { padding: 44px 24px; text-align: center; color: var(--muted); }
  footer { padding: 16px 24px; color: var(--muted); border-top: 1px solid var(--line); line-height: 1.6; }
  @media(max-width: 760px) { form { padding: 18px; gap: 8px; } input { width: calc(100% - 80px); min-width: 0; flex: auto; } }
`;
const Row = styled.article`
  display: grid; grid-template-columns: minmax(180px, 1fr) 160px 130px 160px; align-items: center; gap: 20px; padding: 20px 24px; border-bottom: 1px solid var(--line);
  &:last-of-type { border-bottom: 0; }
  strong { display: block; font-size: 16px; font-weight: 600; overflow-wrap: anywhere; }
  small { display: block; color: var(--muted); margin-top: 6px; }
  .phone { font-variant-numeric: tabular-nums; }
  .badge { width: fit-content; border-radius: 6px; padding: 6px 9px; background: var(--soft); font-size: 12px; }
  .super { color: ${({ theme }) => theme.isDarkMode ? '#dfc291' : '#916725'}; background: ${({ theme }) => theme.isDarkMode ? '#423624' : '#fff4df'}; }
  .action { justify-self: end; }
  @media(max-width: 760px) { padding: 18px; grid-template-columns: minmax(0,1fr) auto; gap: 14px; .phone { grid-column: 1; grid-row: 2; } .badge { grid-column: 2; grid-row: 1; justify-self: end; } .action { grid-column: 2; grid-row: 2; } }
`;

export default function AdminAdministrators() {
  const [input, setInput] = useState('');
  const [query, setQuery] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [denied, setDenied] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const sequence = useRef(0);
  const mutation = useRef(false);

  const load = useCallback(async (search) => {
    const current = ++sequence.current;
    setLoading(true); setError('');
    try {
      const result = await administratorApi('list', { query: search });
      if (current === sequence.current) { setData(result.data); setDenied(false); setQuery(search); }
    } catch (e) {
      if (current === sequence.current) { setError(e.message); setData(null); setDenied(e.code === 403); }
    } finally { if (current === sequence.current) setLoading(false); }
  }, []);
  useEffect(() => { load(''); return () => { sequence.current++; }; }, [load]);

  const changeRole = async (account) => {
    if (mutation.current || account.superAdmin) return;
    const promoting = account.role !== 'admin';
    const action = promoting ? '设为管理员' : '撤销管理员';
    if (!window.confirm(`确定将“${account.name}”（账号 ${account.id}，${account.phone}）${action}？\n${promoting ? '该账号将可以管理比赛和查看验证码。' : '该账号将恢复为普通用户，已有作品和账号信息不受影响。'}`)) return;
    mutation.current = true; setSaving(true); setError(''); setMessage('');
    try {
      const result = await administratorApi('role', { userId: account.id, expectedRole: account.role, role: promoting ? 'admin' : 'user' });
      setMessage(result.msg); await load(query);
    } catch (e) { await load(query); setError(e.message); }
    finally { mutation.current = false; setSaving(false); }
  };

  return <Page>
    <h1>管理员管理</h1>
    <p>查找已注册账号，设置或撤销管理员权限。</p>
    {data && <div className="summary"><span>超级管理员<b>{data.superAdminCount}</b></span><span>普通管理员<b>{data.adminCount - data.superAdminCount}</b></span></div>}
    {error && <div className="notice error" role="alert">{error}</div>}
    {message && <div className="notice" role="status">{message}</div>}
    {!denied && <Panel aria-busy={loading || saving}>
      <form onSubmit={e => { e.preventDefault(); setMessage(''); load(input.trim()); }}>
        <input aria-label="查找账号" placeholder="手机号、昵称或账号编号" maxLength={64} value={input} onChange={e => setInput(e.target.value)} disabled={saving} />
        <button type="submit" className="primary" disabled={loading || saving}>查询</button>
        <button type="button" disabled={loading || saving} onClick={() => { setInput(''); setMessage(''); load(''); }}>全部管理员</button>
        <button type="button" disabled={loading || saving} onClick={() => load(query)}>刷新</button>
      </form>
      <div className="caption">{query ? `查询结果：${query}` : '当前管理员'}{data ? ` · ${data.items.length} 个账号` : ''}</div>
      {loading ? <div className="empty">正在加载…</div> : data?.items.length ? data.items.map(account => <Row key={account.id}>
        <div><strong>{account.name || '未设置昵称'}</strong><small>账号 {account.id}</small></div>
        <div className="phone">{account.phone}</div>
        <span className={`badge ${account.superAdmin ? 'super' : ''}`}>{account.superAdmin ? '超级管理员' : account.role === 'admin' ? '管理员' : '普通用户'}</span>
        <div className="action">{account.superAdmin ? <span className="muted">受保护</span> : <button type="button" disabled={saving} className={account.role === 'admin' ? 'danger' : 'primary'} onClick={() => changeRole(account)}>{account.role === 'admin' ? '撤销管理员' : '设为管理员'}</button>}</div>
      </Row>) : <div className="empty">{error ? '请稍后重试。' : '没有找到账号，请检查手机号或昵称。'}</div>}
      <footer>{data?.hasMore && <>仅显示前50个结果，请输入更准确的手机号、昵称或编号。<br /></>}超级管理员受保护；本页仅管理普通管理员权限。</footer>
    </Panel>}
  </Page>;
}
